import React, { useState } from "react";
import { ShieldCheck, Copy, Check, Lock, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePartner } from "@/context/PartnerContext";
import { useCopy } from "@/lib/copy";
import { encryptForShop, fetchMainShopKey } from "@/lib/pgpClient";

// Encrypts delivery details in the customer's browser and hands back the PGP block to paste into
// the Telegram bot. Plain text never leaves the device; only the shop's private key can open it.
export default function EncryptAddress() {
  const { partner, brand, isPartner, ready } = usePartner();
  const { t, C } = useCopy();
  const [addr, setAddr] = useState({ name: "", address: "", city: "", postcode: "", notes: "" });
  const [block, setBlock] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const keyAvailable = !isPartner || Boolean(partner?.pgp_public_key);
  const field = (k, label, extra = {}) => (
    <div className={extra.full ? "sm:col-span-2" : ""}>
      <Label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
      <Input data-testid={`encrypt-${k}`} autoComplete="off" value={addr[k]} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} placeholder={extra.placeholder} className="mt-1.5 rounded-none" />
    </div>
  );

  const run = async () => {
    if (["name", "address", "city", "postcode"].some((k) => !addr[k].trim())) { toast.error("Please fill in your name, address, town/city and postcode"); return; }
    setBusy(true);
    try {
      const armored = isPartner ? partner.pgp_public_key : await fetchMainShopKey();
      setBlock(await encryptForShop(armored, addr));
      setAddr({ name: "", address: "", city: "", postcode: "", notes: "" });
    } catch (e) {
      toast.error("Couldn't encrypt in your browser — please refresh and try again");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(block); setCopied(true); toast.success("Copied — now paste it into the chat"); }
    catch { document.getElementById("encrypt-output")?.select(); toast.message("Long-press the block and choose Copy"); }
  };

  if (!ready) return null;
  return (
    <div className="mx-auto max-w-2xl px-6 py-12" data-testid="encrypt-page">
      <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary">{t("enc_eyebrow")}</div>
      <h1 className="mt-2 font-heading text-3xl font-black tracking-tight sm:text-4xl">{t("enc_title")}</h1>
      <p className="mt-3 text-sm text-muted-foreground"><C k="enc_intro" /></p>

      {!keyAvailable ? (
        <div className="mt-8 border border-border bg-card p-5 text-sm text-muted-foreground" data-testid="encrypt-no-key">{t("pgp_page_missing_body")}</div>
      ) : !block ? (
        <div className="mt-8 border border-border bg-card p-5" data-testid="encrypt-form">
          <div className="grid gap-3 sm:grid-cols-2">
            {field("name", t("co_auto_name"), { full: true })}
            {field("address", t("co_auto_address"), { full: true })}
            {field("city", t("co_auto_city"))}
            {field("postcode", t("co_auto_postcode"))}
            {field("notes", t("co_auto_notes"), { full: true, placeholder: t("co_auto_notes_placeholder") })}
          </div>
          <button data-testid="encrypt-submit" onClick={run} disabled={busy}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-primary px-5 py-3 font-heading text-sm font-bold text-primary-foreground disabled:opacity-60">
            <Lock className="h-4 w-4" /> {busy ? t("enc_working") : t("enc_button")}
          </button>
          <p className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground"><ShieldCheck className="mt-0.5 h-3 w-3 shrink-0 text-primary" /><span><C k="enc_note" vars={{ brand: brand.name }} /></span></p>
        </div>
      ) : (
        <div className="mt-8 border border-primary/40 bg-card p-5" data-testid="encrypt-result">
          <div className="flex items-center gap-2 font-heading text-base font-bold"><Check className="h-4 w-4 text-primary" /> {t("enc_done_title")}</div>
          <p className="mt-1 text-sm text-muted-foreground"><C k="enc_done_body" /></p>
          <textarea id="encrypt-output" data-testid="encrypt-output" readOnly value={block} rows={8} onFocus={(e) => e.target.select()}
            className="mono mt-3 w-full rounded-none border border-border bg-background p-3 text-[11px] leading-snug" />
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="encrypt-copy" onClick={copy} className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? t("enc_copied") : t("enc_copy")}
            </button>
            <button data-testid="encrypt-again" onClick={() => { setBlock(""); setCopied(false); }} className="inline-flex items-center gap-2 border border-border px-4 py-2.5 text-sm">
              <RotateCcw className="h-4 w-4" /> {t("enc_again")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
